<template>
<!-- `method="POST" so password managers (like LastPass) don't get angry on it. -->
<form class="quick-form authentication-component" @submit.prevent="onSubmit" method="POST">
  <div class="quick-form__icon">
    <IconAuth v-if="!page.startsWith('done')"/>
    <IconSaltBae v-else/>
  </div>
  <!-- `:key` so the transition also plays when the text changes. -->
  <TransitionExpand>
    <h2 :key="headerText" class="quick-form__title">{{ headerText }}</h2>
  </TransitionExpand>
  <TransitionExpand>
    <p
      :key="subtitleText"
      class="quick-form__subtitle authentication-component__subtitle"
    >{{ subtitleText }}</p>
  </TransitionExpand>

  <TransitionExpand>
    <CustomField
      v-show="stage === 'username'"
      label="Телефон"
      class="quick-form__max-width-block authentication-component__field"
    >
      <PhoneInput v-model="phone" required @input:country="phone_country = $event" />
    </CustomField>
  </TransitionExpand>
  <!-- Pay attention to form validation. E.g. If a field is rendered, not filled, and is required, validation will fail
  and the user will be stuck on the current stage. -->
  <TransitionExpand>
    <PasswordInput
      v-show="stage === 'password'"
      v-model="password"
      :label="page === 'resetPassword' ? 'Новый пароль' : 'Пароль'"
      :autocomplete="page === 'signIn' ? 'current-password' : 'new-password'"
      :minlength="page === 'signIn' ? undefined : '8'"
      :required="stage !== 'username'"
      class="quick-form__max-width-block authentication-component__field"
    />
  </TransitionExpand>
  <TransitionExpand>
    <SmsCodeInput
      v-if="stage === 'code'"
      required
      v-model="code"
      @input="e => { if (e.length === 4) onSubmit() }"
      class="authentication-component__field"
    />
  </TransitionExpand>

  <TransitionExpand>
    <span
      v-if="error"
      :key="error"
      class="quick-form__error"
    >{{ error }}</span>
  </TransitionExpand>

  <button
    v-if="!page.startsWith('done')"
    type="submit"
    class="btn btn-primary btn-sm btn-block btn-rounded quick-form__max-width-block"
    :disabled="busy"
    key="submit"
  >
    <i class="fal fa-spinner fa-spin" v-if="busy"></i>
    <template>{{ submitButtonLabel }}</template>
  </button>
  <template v-else>
    <router-link
      :to="{ name: 'catalog' }"
      class="btn btn-primary btn-sm btn-block btn-rounded quick-form__max-width-block"
    >Перейти в каталог</router-link>
    <router-link :to="{ name: 'dashboard' }" class="quick-form__text-button">Личный кабинет</router-link>
  </template>

  <TransitionExpand>
    <LegalNotice
      v-if="showLegalNotice"
      class="authentication-component__legal-notice quick-form__max-width-block"
    />
  </TransitionExpand>

  <!--
  <router-link
    :to=""
    class="quick-form__text-button"
  >Личный кабинет</router-link>
  -->

  <TransitionExpand>
    <button
      v-if="page === 'signIn' && stage !== 'username'"
      @click="page = 'resetPassword'; stage = 'password'"
      type="button"
      class="quick-form__text-button"
      key="resetPassword"
    >Не помню пароль</button>
  </TransitionExpand>
  <TransitionExpand>
    <template v-if="stage === 'code'">
      <button
        v-if="spamTimeout <= 0"
        @click="resendCode"
        :disabled="busy"
        class="quick-form__text-button"
        type="button"
        key="resend"
      >Выслать код повторно</button>
      <SmsCooldownText v-else :value="spamTimeout" class="authentication-component__sms-cooldown-text"/>
    </template>
  </TransitionExpand>
  <TransitionExpand>
    <button
      v-if="stage !== 'username' && !page.startsWith('done')"
      @click="page = 'signIn'; stage = 'username';"
      class="quick-form__text-button"
      type="button"
      key="changeLogin"
    >Ввести другой номер</button>
  </TransitionExpand>
</form>
</template>

<script>
import { mapGetters } from 'vuex';
import TransitionExpand from '@/components/AsyncTransitionExpand';
import CustomField from '@/components/inputs/CustomField';
import PhoneInput from '@/components/inputs/PhoneInput';
import SmsCooldownText from '@/components/SmsCooldownText.vue';

export default {
  components: {
    CustomField,
    PhoneInput,
    TransitionExpand,
    SmsCooldownText,
    SmsCodeInput: () => import('@/components/inputs/SmsCodeInput'),
    PasswordInput: () => import('./PasswordInput'),
    LegalNotice: () => import(/* webpackPreload: true */ '@/components/legal/LegalNotice'),
    IconAuth: () => import(/* webpackPreload: true */ '@/assets/images/icons/icon-auth.svg?inline'),
    IconSaltBae: () => import('@/assets/images/icons/icon-salt-bae.svg?inline'),
  },

  created() {
    ['page', 'stage', 'phone', 'password', 'code'].forEach(propName => this.$watch(propName, () => this.error = null));
  },

  computed: {
    ...mapGetters({
      hasUser: 'auth/hasUser',
    }),

    headerText() {
      switch(this.page) {
        case 'signIn':
          return 'Вход на сайт';
        case 'signUp':
          return 'Регистрация';
        case 'resetPassword':
          return 'Сменить пароль';
        default: {
          if (this.page.startsWith('done')) {
            return 'Спасибо!';
          }
          break
        }
      }

      this.thowUnexpectedPageAndStageCombination();
    },

    subtitleText() {
      const loggedInSuccessfullyText = 'Вы успешно вошли';
      switch (this.page) {
        case 'signIn':
          switch (this.stage) {
            case 'username':
              return 'Введите телефон, указанный при регистрации';
            case 'password':
              return 'Введите пароль, указанный при регистрации';
            default:
              this.thowUnexpectedPageAndStageCombination();
              break
          }
          break
        case 'signUp':
          switch (this.stage) {
            case 'password':
              return 'Придумайте новый пароль для входа на сайт';
            case 'code':
              return `Введите SMS-код,\nмы отправили его на номер\n${this.phone}`;
            default:
              this.thowUnexpectedPageAndStageCombination();
              break
          }
          break
        case 'resetPassword':
          switch (this.stage) {
            case 'password':
              return 'Придумайте новый пароль для входа на сайт';
            case 'code':
              return `Подтвердите смену пароля,\nвведите SMS-код, отправленный на\n${this.phone}`;
            default:
              this.thowUnexpectedPageAndStageCombination();
          }
          break
        case 'done:resetPassword':
          return 'Вы успешно сменили пароль и вошли';
        case 'done:signIn':
          return loggedInSuccessfullyText;
        case 'done:signUp':
          return loggedInSuccessfullyText;
        default:
          this.thowUnexpectedPageAndStageCombination();
          break
      }
    },

    submitButtonLabel() {
      switch (this.page) {
        case 'signIn':
          return 'Войти';
        case 'signUp':
          return 'Зарегистрироваться';
        case 'resetPassword':
          return 'Сменить пароль';
      }

      this.thowUnexpectedPageAndStageCombination();
    },

    showLegalNotice() {
      return !this.userHasAcceptedPolicies
        && this.page === 'signIn' && this.stage === 'username'; // Because every path starts with this page + stage.
    },
  },

  methods: {
    onSubmit() {
      return this.performActionFor({ page: this.page, stage: this.stage });
    },

    async performActionFor({ page, stage }) {
      const beenShowingLegalNoticeOnSubmitStart = this.showLegalNotice;
      this.error = null;
      let p;

      switch (page) {
        case 'signIn':
          p = stage === 'username' ? this.onUsernameSubmit() : this.onSignInPasswordSubmit();
          break;
        case 'signUp':
          p = stage === 'password' ? this.onSignUpPasswordSubmit() : this.onSignUpCodeSubmit();
          break;
        case 'resetPassword':
          p = stage === 'password' ? this.onResetPasswordNewPasswordSubmit() : this.onResetPasswordCodeSubmit();
          break;
      }

      this.busy = true;

      try {
        await p;
      } catch(err) {
        this.error = this.getAuthErrorMessage(err.response.data || null)
        throw err;
      } finally {
        this.busy = false;
      }

      if (beenShowingLegalNoticeOnSubmitStart) {
        this.userHasAcceptedPolicies = true;
      }
    },

    getAuthErrorMessage(data) {
      if (!data || !data.error || !data.error.error_code) {
        return 'Ошибка'
      } else if (data.error.message !== data.error.original_message) {
        return data.error.message
      }

      switch (data.error.error_code) {
        case 'security.ban':
          return 'Ваш IP-адрес заблокирован. Напишите на tech@zenky.io, если считаете, что это ошибка.';
      }

      return 'Ошибка'
    },

    // Not using a generator for these functions because we want import paths to be static so importing works better.
    async onUsernameSubmit() {
      console.log('onUsernameSubmit')
      return (await import('./methods/signIn/onUsernameSubmit')).default.call(this);
    },

    async onSignInPasswordSubmit() {
      return (await import('./methods/signIn/onPasswordSubmit')).default.call(this);
    },

    async onResetPasswordNewPasswordSubmit() {
      return (await import('./methods/resetPassword/onNewPasswordSubmit')).default.call(this);
    },

    async onResetPasswordCodeSubmit() {
      return (await import('./methods/resetPassword/onCodeSubmit')).default.call(this);
    },

    async onSignUpPasswordSubmit() {
      return (await import('./methods/signUp/onPasswordSubmit')).default.call(this);
    },

    async onSignUpCodeSubmit() {
      return (await import('./methods/signUp/onCodeSubmit')).default.call(this);
    },

    async resendCode() {
      // Just do the same thing as the submit button on the previous stage.
      const prevStage = 'password';
      await this.performActionFor({ page: this.page, stage: prevStage });
    },

    thowUnexpectedPageAndStageCombination() {
      throw new Error(`Unexpected page & stage combination: "${this.page}", "${this.stage}"`);
    },
  },

  watch: {
    page(val) {
      // Some parts of the template only check what stage it is, not what page.
      if (val.startsWith('done')) {
        this.stage = null;
      }
    },

    hasUser(val) {
      if (val === false) {
        Object.assign(this.$data, this.$options.data.call(this));
      }
    },
  },

  /**
   * @returns {{
   *   page: 'signIn' | 'signUp' | 'resetPassword' | 'done:resetPassword' | 'done:signIn' | 'done:signUp',
   *   stage: 'username' | 'password' | 'code',
   * }}
   */
  data() {
    return {
      page: 'signIn',
      stage: 'username',
      phone: '',
      phone_country: '',
      password: '',
      code: '',
      error: null,
      busy: false,
      isPhoneValidAccordingToBackend: null,
      userRegistrationStatus: null,
      userHasAcceptedPolicies: false,
      spamTimeout: 0,
      spamTimeoutTimerId: -1,
      phoneCheckerTimeoutId: null,
    };
  },
};
</script>

<style lang="less">
@import '~@/assets/less/components/quick-form';

.authentication-component {
  &__subtitle {
    @apply whitespace-pre-line;
  }
  &__legal-notice {
    @apply text-xs font-light opacity-60 text-left my-3;
    a {
      @apply text-primary;
    }
  }
  &__field {
    @apply my-2;
  }
  .quick-form__text-button {
    @apply my-2 p-0; // So TransitionExpand isn't jerky.
  }
  &__sms-cooldown-text {
    @apply my-2;
  }
}
</style>
